/**
 * Loyalty-club currency converter.
 *
 * Liquid sections (loyalty-benefits, loyalty-tiers) render amounts in EUR
 * (the shop's base currency) and tag them with data-vv-money +
 * data-vv-eur="<number>" + data-vv-prefix="...". This script rewrites them
 * in the customer's active Shopify presentment currency at page load —
 * e.g. a Danish customer sees "kr 2.058" where a Dutch customer sees "€270".
 *
 * Why JS and not Liquid? Shopify does NOT expose cart.currency.rate to
 * Liquid; the conversion rate (Shopify.currency.rate) is only available
 * via JS. So the page server-renders the EUR fallback, and we upgrade it
 * in-place on hydration.
 *
 * Re-runs on Shopify Markets currency-switch events (best-effort —
 * Shopify usually full-reloads on switch anyway).
 */
(function () {
  function getActive() {
    return (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR'
  }

  function getRate() {
    const r = window.Shopify && window.Shopify.currency && window.Shopify.currency.rate
    const n = parseFloat(r)
    return Number.isFinite(n) && n > 0 ? n : 1
  }

  /**
   * Round an amount UP to a "nice" presentation number.
   *
   * Used after currency conversion so customers see clean thresholds like
   * "Spend 2.100 kr" instead of raw 2.058,11 kr. ALWAYS rounds upward —
   * the threshold should never be quoted lower than the true conversion,
   * otherwise a customer who hits the displayed amount may not have hit
   * the actual loyalty threshold yet.
   *
   * Granularity scales with magnitude — small amounts step by 1, large
   * amounts step by 500. The breakpoints keep the rounding "lossiness"
   * around 1–2% so converted values stay accurate enough to be honest.
   */
  function roundUpNice(amount) {
    if (amount <= 0) return 0
    if (amount < 5) return Math.ceil(amount) //                     → 1, 2, 3, 4, 5
    if (amount < 50) return Math.ceil(amount / 5) * 5 //             → 5, 10, 15, ..., 50
    if (amount < 500) return Math.ceil(amount / 50) * 50 //          → 50, 100, ..., 500
    if (amount < 10000) return Math.ceil(amount / 100) * 100 //      → 100, 200, ..., 9900, 10000
    return Math.ceil(amount / 500) * 500 //                          → 10500, 11000, ...
  }

  /**
   * Format an amount in the customer's currency. Uses Intl.NumberFormat
   * for proper symbol + decimal-style per locale. Hides trailing zeros
   * on round amounts (e.g. shows "kr 2.058" not "kr 2.058,00") to match
   * the visual cleanliness of money_without_trailing_zeros in Liquid.
   *
   * precision overrides decimal handling:
   *   - undefined / 2 → 2 decimals (default, good for small amounts)
   *   - 0 → round to whole unit, no decimals (good for spend thresholds —
   *         "Spend 2.058 kr" reads cleaner than "Spend 2.058,11 kr")
   */
  function formatMoney(amount, currency, precision) {
    let displayAmount = amount
    let minDecimals
    let maxDecimals

    if (precision === 0) {
      displayAmount = Math.round(amount)
      minDecimals = 0
      maxDecimals = 0
    } else {
      const isRound = Math.abs(amount - Math.round(amount)) < 0.005
      minDecimals = isRound ? 0 : 2
      maxDecimals = 2
    }

    try {
      // Prefer the customer's browser locale so number grouping looks native
      // (1.340 in DK, 1,340 in en-US, etc.). Fallback to a sensible EU locale.
      return new Intl.NumberFormat(navigator.language || 'da-DK', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: minDecimals,
        maximumFractionDigits: maxDecimals,
      }).format(displayAmount)
    } catch (e) {
      // Pathological browser — fall back to plain "EUR 1234.56" string
      return currency + ' ' + displayAmount.toFixed(maxDecimals)
    }
  }

  function convert() {
    const active = getActive()
    const rate = getRate()
    // We don't early-return for EUR/rate=1 anymore. The server renders a
    // plain "€X" fallback using the raw setting value (e.g. "€0.3" for 0.30),
    // which loses trailing zeros and uses dot-decimal regardless of locale.
    // Running JS unconditionally gives consistent Intl-formatted output
    // (€0,30 / kr 3 / 2 100 kr — symbol + decimals + grouping per locale).

    document.querySelectorAll('[data-vv-money]').forEach((el) => {
      const eur = parseFloat(el.getAttribute('data-vv-eur') || '0')
      if (!eur) return
      const prefix = el.getAttribute('data-vv-prefix') || ''
      const precisionAttr = el.getAttribute('data-vv-precision')
      const precision = precisionAttr == null ? undefined : parseInt(precisionAttr, 10)

      let converted = eur * rate
      let effectivePrecision = precision

      // Round up to a "nice" presentation number for non-EUR currencies.
      // EUR is the shop's base — the merchant entered exact EUR values, so
      // we show them verbatim. Conversions to DKK/SEK/NOK/etc. otherwise
      // surface ugly fractions like "Spend 2.058,11 kr" that look amateurish
      // and inconsistent across customers.
      if (active !== 'EUR') {
        converted = roundUpNice(converted)
        // After roundUpNice() the value is always whole, so force precision
        // to 0 — no point rendering "kr 2.100,00" when 2.100 is exact.
        effectivePrecision = 0
      }

      el.textContent = prefix + formatMoney(converted, active, effectivePrecision)
    })
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', convert)
  } else {
    convert()
  }

  // Re-run if the theme/Markets dispatches a currency change without a
  // full reload (rare, but cheap to handle).
  document.addEventListener('currency:changed', convert)
})()
